import { Component, Inject, input, OnInit, ChangeDetectorRef } from '@angular/core';
import { ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { CommonModule, Location } from '@angular/common';

import moment, { Moment } from 'moment';
import { GameDetailsViewModel } from '../../../shared/models/GameModels';
import { Report, ReportViewModel, ReportText, ReportSkill, ScoutingReportMasterData } from '../../../shared/models/ReportModels';
import { PlayerDetailsViewModel, PlayerPosition } from '../../../shared/models/player.model';
import { GameService } from '../../../shared/services/game.service';
import { PlayerService } from '../../../shared/services/player.service';
import { ScoutingPlayerService } from '../../../shared/services/scouting-player.service';
import { ScoutingService } from '../../../shared/services/scouting.service';
import { MaterialModule } from '../../../material/material.module';
import { GameHeaderSmallComponent } from '../../components/game-header-small/game-header-small.component';
import { PlayerHeaderComponent } from '../../components/player-header/player-header.component';
import { RatingSlider5Component } from '../../components/rating-slider5/rating-slider5.component';
import { TranslatePipe } from '@ngx-translate/core';



@Component({
    selector: 'app-edit-report',
    imports: [CommonModule, MaterialModule, GameHeaderSmallComponent, PlayerHeaderComponent, ReactiveFormsModule, RatingSlider5Component, TranslatePipe],
    templateUrl: './edit-report.component.html',
    styleUrls: ['./edit-report.component.css']
})
export class EditReportComponent implements OnInit {
  PlayerId: string;
  GameId: string;
  TeamId: string;
  _reportId: string;
  Game: GameDetailsViewModel;
  Player: PlayerDetailsViewModel;
  ReportMasterData: ScoutingReportMasterData;
  Report: ReportViewModel;
  reportForm: UntypedFormGroup;
  PlayerPositions: PlayerPosition[];

  mylist = [0,1,2,3,4,5];
  gameperf_texts=['-','Bad', 'Average','Good', 'Very good', 'Excellent' ];
  gameperf_colors= ['','#ff2c1c','#e9c400','yellowgreen', 'limegreen', '#1e90ff'];
  
  isLoading = true;
  loadingError: string | null = null;

  ReportId = input<string>();

  constructor(
    private route: ActivatedRoute, 
    private _formBuilder: UntypedFormBuilder,
    private _adapter: DateAdapter<any>,
    private snackbar: MatSnackBar,
    public location: Location,
    @Inject(MAT_DATE_LOCALE) private _locale: string,
    private _scoutingplayerService: ScoutingPlayerService,
    private _reportService: ScoutingService,
    private _gameService: GameService,
    private _playerService: PlayerService,
    private cdr: ChangeDetectorRef) { 
  }

  ngOnInit(): void {
    // console.log("=== EditReportComponent ngOnInit START ===");
    
    //this._adapter.setLocale(localStorage.getItem("language")?.substring(0, 2) ?? 'en');  
    this._adapter.setLocale('sv');  

    // console.log("EditReportComponent ngOnInit: ReportId from input - " + this.ReportId());
    //This is for opening an old report
    if (!this.ReportId()) {
      this._reportId = this.route.snapshot.paramMap.get('ReportId') ?? "";
      // console.log("EditReportComponent: Got ReportId from route params - " + this._reportId);
    } else {
      this._reportId = this.ReportId() ?? "";
      // console.log("EditReportComponent: Got ReportId from input signal - " + this._reportId);
    }

    // console.log("EditReportComponent: Final ReportId to use - " + this._reportId);

    if (this._reportId) {
        //Get report
      // console.log("EditReportComponent: Fetching report data for ReportId - " + this._reportId);
      // console.log("EditReportComponent: API URL - " + this._reportService['rootUrl']);
      this.isLoading = true;
      this.loadingError = null;
      
      // Timeout safety net
      const timeoutId = setTimeout(() => {
        if (this.isLoading && !this.Report) {
          // console.error("EditReportComponent: ⏱️ API TIMEOUT - Request took longer than 30 seconds");
          this.loadingError = "Request timed out - please check your connection";
          this.isLoading = false;
        }
      }, 30000);

      this._playerService.getPlayerPositionsByUserOrganization().subscribe(positions=>{
        this.PlayerPositions = positions;
      });
      
      this._reportService.getReport(this._reportId).subscribe({
        next: (report) => {
          clearTimeout(timeoutId);
          // console.log("EditReportComponent: ✅ Report API SUCCESS", report);
          this.Report = report;
          // console.log("EditReportComponent: Report data loaded successfully", this.Report);
          // console.log("EditReportComponent: this.Report is now:", this.Report ? "SET" : "NULL/UNDEFINED");

          //Prepare model for Player details component in UI
          var tmpPlayer: PlayerDetailsViewModel;
          tmpPlayer = new PlayerDetailsViewModel();
          tmpPlayer.Player = this.Report.SCReport.Player;
          tmpPlayer.PlayerAgeDays = this.Report.PlayerAgeDays;
          tmpPlayer.PlayerAgeMonths = this.Report.PlayerAgeMonths;
          tmpPlayer.PlayerAgeYears = this.Report.PlayerAgeYears;
          this.Player = tmpPlayer;

          // Validate position ID before making API call
          const positionId = report.SCReport.Player?.PositionId;
          if (!positionId) {
            // console.error("EditReportComponent: Player has no PositionId", report.SCReport.Player);
            this.loadingError = "Player position data is missing";
            this.isLoading = false;
            this.openSnackBar("Unable to load form: Player position is missing", "CLOSE");
            return;
          }

          //Get masterdata
          this._scoutingplayerService.getReportMasterDataForPosition(positionId.toString()).subscribe({
            next: (res) => {
            this.ReportMasterData = res;
            // console.log("EditReportComponent: Master data loaded successfully", res);
            //Prepare form data
            this.reportForm = this._formBuilder.group({
              PlayerId: report.SCReport.Player.LifelineScoutingId,
              GameId: report.SCReport.GameId,
              Scout: report.SCReport.Userid,  
              Capacity: report.PlayerCapacity?.Ranking,  
              PlayerType: report.PlayerType?.Ranking,  
              PositionId: report.SCReport.PositionId,        
              GamePerformance: report.GamePerformance.Ranking,  
              CurrentLevelRanking: report.SCReport.CurrentLevelRanking,
              CurrentLevelRankingModel: this.ReportMasterData.CurrentLevelRankingModel,
              Projection: [report.TotalQuality.Ranking, [Validators.required]],  
              Comment: [this.getTextType(1), [Validators.required]],  
              Location: [report.SCReport.Location, [Validators.required]],  
              ScoutingSourceId: [report.SCReport.ScoutingSourceId, [Validators.required]],
              //ReportDate: [moment(report.SCReport.ReportDate), [Validators.required]],  
              ReportDate: [report.SCReport.ReportDate, [Validators.required]],  
              SkillGroups: this._formBuilder.array([              
              ])
            });

          
            //Add skills to form array
            var skillgroups = this.reportForm.get('SkillGroups') as UntypedFormArray;
            this.ReportMasterData.SkillsGroupsView.forEach(group => {
              //console.log(group);
              var skills = this._formBuilder.array([]);
              group.Skills.forEach(skill => {
                //console.log(skill);
                //get value from report data for this skill
                var tmpValue: number;
                tmpValue = this.findSkillValueById(skill.SkillId);
                //console.log("tmpValue:" + tmpValue);
                //Set value for the UI, so colors are set on button toggle group
                //this['selected'+skill.SkillId]=tmpValue;
                skills.push(this._formBuilder.group({
                    SkillGroupId: group.SkillGroupId,
                    SkillId: skill.SkillId,
                    Value: tmpValue
                  })
                );
              });
              //Push to array
              skillgroups.push(
                this._formBuilder.group({
                  SkillGroupId: group.SkillGroupId,
                  Skills: skills
                })
              );
    
            });
            
            this.isLoading = false;            
            this.cdr.detectChanges();
            

          },
          error: (error) => {
            // console.error("EditReportComponent: Error loading master data", error);
            this.loadingError = "Failed to load master data for position";
            this.isLoading = false;
            this.openSnackBar("Failed to load master data for position", "CLOSE");
          }
        });

         


          
        },
        error: (error) => {
          // console.error("EditReportComponent: ❌ Report API ERROR", error);
          // console.error("EditReportComponent: Error status:", error.status);
          // console.error("EditReportComponent: Error message:", error.message);
          // console.error("EditReportComponent: Full error object:", JSON.stringify(error, null, 2));
          this.loadingError = "Failed to load report data";
          this.isLoading = false;
          this.openSnackBar("Failed to load report data", "CLOSE");
        },
        complete: () => {
          // console.log("EditReportComponent: Report API call COMPLETED");
        }
      });




          


    } else {
      // console.error("EditReportComponent: No ReportId provided");
      this.loadingError = "No report ID provided";
      this.isLoading = false;
      this.openSnackBar("No report ID provided", "CLOSE");
    }
  }

  getTextType(typeId: number): string{
    var text= this.Report.SCReport.ReportTexts.find(text=>text.TextTypeId==typeId);
    if(text==undefined)
      return "";
    else
      return text.Text;

  }

  //Function for findind value for a spcific skillid, if exists
  findSkillValueById = (id: number) => {   
    for (const group of this.Report.SkillGroups) {
      const result = group.Skills.find(skill => skill.SkillId === id);
      if (result) return result.Ranking;
    }
    return 0;
  }

  onSubmit(){
    //console.log('submit');
    var newReport = new Report;
    newReport.ReportId = this.Report.SCReport.ReportId;
    if(this.Report.Game)
      newReport.GameId = this.Report.Game.GameId;
    newReport.PlayerId = this.Report.SCReport.PlayerId;
    newReport.ReportSkills = [];
    newReport.ReportTexts = [];
    newReport.Location = this.reportForm.get('Location')?.value;
    newReport.ScoutingSourceId = this.reportForm.get('ScoutingSourceId')?.value;
    newReport.PositionId = this.reportForm.get('PositionId')?.value;    
    newReport.ReportDate = moment(this.reportForm.get('ReportDate')?.value).toDate();

    if(this.ReportMasterData.CapacityEnabled){
      newReport.CurrentLevelRanking = this.reportForm.get('CurrentLevelRanking')?.value;
      newReport.CurrentLevelRankingModel = this.ReportMasterData.CurrentLevelRankingModel;
    }
    

    var comments = new ReportText();
    comments.Text = this.reportForm.get('Comment')?.value;
    comments.TextTypeId = 1;
    newReport.ReportTexts.push(comments);

    var projSkill = new ReportSkill;    
    projSkill.Ranking = this.reportForm.get('Projection')?.value;
    projSkill.SkillGroupId = 0;
    projSkill.SkillId = 1;
    newReport.ReportSkills.push(projSkill);

    var gamePerformance = new ReportSkill;    
    gamePerformance.Ranking = this.reportForm.get('GamePerformance')?.value;
    gamePerformance.SkillGroupId = 0;
    gamePerformance.SkillId = 14;
    newReport.ReportSkills.push(gamePerformance);


    if(this.ReportMasterData.CapacityEnabled){
      var capacitySkill = new ReportSkill;    
      capacitySkill.Ranking = this.reportForm.get('Capacity')?.value;
      capacitySkill.SkillGroupId = 0;
      capacitySkill.SkillId = 24;
      newReport.ReportSkills.push(capacitySkill);
    }


    if(this.ReportMasterData.PlayerTypeEnabled){
      var playerType = new ReportSkill;    
      playerType.Ranking = this.reportForm.get('PlayerType')?.value;
      playerType.SkillGroupId = 0;
      playerType.SkillId = 25;
      newReport.ReportSkills.push(playerType);
    }

    


    var groups = this.reportForm.get('SkillGroups') as UntypedFormArray;
    groups.controls.forEach(group => {
      var skills = group.get('Skills') as UntypedFormArray;
      skills.controls.forEach(skill=>{
        var newSkill = new ReportSkill;
        newSkill.Ranking = skill.get('Value')?.value;
        newSkill.SkillGroupId = newSkill.SkillId = skill.get('SkillGroupId')?.value;
        newSkill.SkillId = skill.get('SkillId')?.value;
        newReport.ReportSkills.push(newSkill);        
      });
    });
    // console.log(newReport);
    // console.log(this.reportForm.value);
    this._reportService.saveReport(newReport).subscribe(result=>{
      if(result){
        this.openSnackBar("Report saved", "CLOSE");
        this.location.back();
      }else{
        this.openSnackBar("Failed to save report", "CLOSE");
      }
        
    });
  }
  openSnackBar(message: string, action: string) {
    this.snackbar.open(message, action,{
      duration: 2000
    });
  }
  
  getText(value: number){
    if (value == 0) {
      return '';
    }
    if (value == 1) {
      return 'Bad';
    }
    if (value == 2) {
      return 'Average';
    }
    if (value == 3) {
      return 'Good';
    }
    if (value == 4) {
      return 'Very good';
    }
    if (value == 5) {
      return 'Excellent';
    }


    return value;
  }

  getProjectionColor(value: number){
    var ProjColors = ['lightgray', '#ff2c1c','#e9c400','#e9c400','limegreen','limegreen','limegreen','limegreen','limegreen'];
    return ProjColors[Math.floor(value)];
  }

  getCapacityColor(value: number){
    
    var ProjColors = ['lightgray', '#ff2c1c','#e9c400','#e9c400','#e9c400','limegreen','limegreen','limegreen','limegreen','limegreen'];
    return ProjColors[Math.floor(value)]      
  }

  getCapacityText(value: number){
    if(value==null)
      return '';
    else
      return this.ReportMasterData.Capacity.filter(cap=>cap.Ranking==value)[0].Text;
  }

getCurrentLevelText(value: number){
    if(value==null){
      return '';
    }else {
       return this.ReportMasterData.CurrentLevel.filter(level=>level.Ranking==value)[0].Text;
    }
  }

  getProjectionText(value: number){
    if(value==null){
      return '';
    }else {
       return this.ReportMasterData.Projection.filter(cap=>cap.Ranking==value)[0].Text;
    }
  }
   

}
